import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { IoIosArrowForward } from "react-icons/io";
import { LuLayoutDashboard } from "react-icons/lu";
import { RiMoneyDollarCircleLine } from "react-icons/ri";
import { FaHubspot } from "react-icons/fa";
import { topItems, masterItems, financeItems, dashboardItems } from './menuItems';

/**
 * Sidebar — every menu item is only shown if the logged-in user
 * has that page key in their UserAccess.pages array (set via Access Panel).
 * Superadmin always sees everything.
 */
const Sidebar = ({ isCollapsed }) => {
  const location = useLocation();
  const { canAccessPage } = useAuth();
  const [masterOpen, setMasterOpen] = useState(
    location.pathname.startsWith('/master')
  );
  const [dashboardOpen, setDashboardOpen] = useState(
    location.pathname.startsWith('/dashboard')
  );
  const [financeOpen, setFinanceOpen] = useState(
    location.pathname.startsWith('/finance')
  );

  // Filter items by page access from Access Panel
  const visibleTop = topItems.filter(i => canAccessPage(i.key));
  const visibleMaster = masterItems.filter(i => canAccessPage(i.key));
  const showMaster = visibleMaster.length > 0;
  const isMasterActive = location.pathname.startsWith('/master');
  const visibleDashboard = dashboardItems.filter(i => canAccessPage(i.key));
  const showDashboard = visibleDashboard.length > 0;
  const isDashboardActive = location.pathname.startsWith('/dashboard');
  const visibleFinance = financeItems.filter(i => canAccessPage(i.key));
  const showFinance = visibleFinance.length > 0;
  const isFinanceActive = location.pathname.startsWith('/finance');

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <Link to="/home" className="sidebar-brand" title="IOEN - Inspection Operations & Execution Network">
        <FaHubspot className="sidebar-brand-icon" />
        {!isCollapsed && <span className="sidebar-brand-text">IOEN</span>}
      </Link>
      <div className="sidebar-menu">
        <ul>
          {/* ── Top items ──────────────────────────────────────── */}
          {visibleTop.map(item => (
            <React.Fragment key={item.path}>
              <li className={location.pathname === item.path ? 'active' : ''}>
                <Link to={item.path} title={isCollapsed ? item.name : ''}>
                  <span className="icon">{item.icon}</span>
                  {!isCollapsed && <span className="text">{item.name}</span>}
                </Link>
              </li>

              {/* ── Dashboard group (under Home) ─────────────────── */}
              {item.key === 'dashboard' && showDashboard && (
                <>
                  <li
                    className={`sidebar-group-header ${isDashboardActive ? 'active' : ''} ${isCollapsed ? 'collapsed' : ''}`}
                    onClick={() => setDashboardOpen(o => !o)}
                    title={isCollapsed ? 'Dashboard' : ''}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <span className="icon"><LuLayoutDashboard size={18} /></span>
                    {!isCollapsed && (
                      <>
                        <span className="text">Dashboard</span>
                        <IoIosArrowForward
                          style={{
                            marginLeft: 'auto',
                            fontSize: 16,
                            display: 'inline-block',
                            transition: 'transform .2s',
                            transform: dashboardOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                          }}
                        />
                      </>
                    )}
                  </li>
                  {(dashboardOpen || isCollapsed) && visibleDashboard.map(dItem => (
                    <li
                      key={dItem.path}
                      className={`sidebar-sub-item ${location.pathname === dItem.path ? 'active' : ''}`}
                    >
                      <Link to={dItem.path} title={isCollapsed ? dItem.name : ''}>
                        <span className="icon">{dItem.icon}</span>
                        {!isCollapsed && <span className="text">{dItem.name}</span>}
                      </Link>
                    </li>
                  ))}
                </>
              )}

              {/* ── Finance group (under Home) ─────────────────── */}
              {item.key === 'dashboard' && showFinance && (
                <>
                  <li
                    className={`sidebar-group-header ${isFinanceActive ? 'active' : ''} ${isCollapsed ? 'collapsed' : ''}`}
                    onClick={() => setFinanceOpen(o => !o)}
                    title={isCollapsed ? 'Finance' : ''}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <span className="icon"><RiMoneyDollarCircleLine size={18} /></span>
                    {!isCollapsed && (
                      <>
                        <span className="text">Finance</span>
                        <IoIosArrowForward
                          style={{
                            marginLeft: 'auto',
                            fontSize: 16,
                            display: 'inline-block',
                            transition: 'transform .2s',
                            transform: financeOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                          }}
                        />
                      </>
                    )}
                  </li>
                  {(financeOpen || isCollapsed) && visibleFinance.map(fItem => (
                    <li
                      key={fItem.path}
                      className={`sidebar-sub-item ${location.pathname === fItem.path ? 'active' : ''}`}
                    >
                      <Link to={fItem.path} title={isCollapsed ? fItem.name : ''}>
                        <span className="icon">{fItem.icon}</span>
                        {!isCollapsed && <span className="text">{fItem.name}</span>}
                      </Link>
                    </li>
                  ))}
                </>
              )}

              {/* ── Master group (under Home) ─────────────────── */}
              {item.key === 'dashboard' && showMaster && (
                <>
                  <li
                    className={`sidebar-group-header ${isMasterActive ? 'active' : ''} ${isCollapsed ? 'collapsed' : ''}`}
                    onClick={() => setMasterOpen(o => !o)}
                    title={isCollapsed ? 'Master' : ''}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <span className="icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M0 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2h2a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1z" />
                      </svg>
                    </span>
                    {!isCollapsed && (
                      <>
                        <span className="text">Master</span>
                        <IoIosArrowForward
                          style={{
                            marginLeft: 'auto',
                            fontSize: 16,
                            display: 'inline-block',
                            transition: 'transform .2s',
                            transform: masterOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                          }}
                        />
                      </>
                    )}
                  </li>

                  {(masterOpen || isCollapsed) && visibleMaster.map(mItem => (
                    <li
                      key={mItem.path}
                      className={`sidebar-sub-item ${location.pathname === mItem.path ? 'active' : ''}`}
                    >
                      <Link to={mItem.path} title={isCollapsed ? mItem.name : ''}>
                        <span className="icon">{mItem.icon}</span>
                        {!isCollapsed && <span className="text">{mItem.name}</span>}
                      </Link>
                    </li>
                  ))}
                </>
              )}

            </React.Fragment>
          ))}
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;
